import React, { useMemo } from 'react';
import { isFeatureEnabledForCurrentTenant } from '@warehouse/shared/config';
import { OptionalInherited } from '@nexspec/warehouse-shared-types/src/titles/TitleMetadata/Inherited';
import styled from 'styled-components';
import CustomMetadataBase, { CustomMetadataColumn } from './CustomMetadata';
import useTitleAutoSave from '../../../hooks/useTitleAutoSave/useTitleAutoSave';
import useMemoJsonPath from '../../../hooks/useMemoJsonPath';
import { fromBasic } from '../../../../../utils/titleGetProperty';

interface Props {
	selectedLocalizedInfo: string | undefined;
}
const LocalizedWrapper = styled.div`
	.table-wrapper {
		margin-left: 6px;
		margin-right: 6px;
		width: auto !important;
	}
`;
export function LocalizedCustomMetadata({ selectedLocalizedInfo }: Props) {
	const stringAutoSave = useTitleAutoSave<OptionalInherited<string>>({
		label: 'Custom metadata string',
		path: useMemoJsonPath(
			fromBasic(['localizedInfos', selectedLocalizedInfo || '__default', 'terms', 'customFieldExample']),
			selectedLocalizedInfo,
		),
	});

	const booleanAutoSave = useTitleAutoSave<OptionalInherited<boolean>>({
		label: 'Custom metadata boolean',
		path: useMemoJsonPath(
			fromBasic(['localizedInfos', selectedLocalizedInfo || '__default', 'terms', 'customBooleanField']),
			selectedLocalizedInfo,
		),
	});

	const pullQuoteAutoSave = useTitleAutoSave<OptionalInherited<string>>({
		label: 'Custom metadata pull quote',
		path: useMemoJsonPath(
			fromBasic(['localizedInfos', selectedLocalizedInfo || '__default', 'terms', 'pullQuote']),
			selectedLocalizedInfo,
		),
	});

	const staffDescriptionAutoSave = useTitleAutoSave<OptionalInherited<string>>({
		label: 'Custom metadata staff description',
		path: useMemoJsonPath(
			fromBasic(['localizedInfos', selectedLocalizedInfo || '__default', 'terms', 'staffDescription']),
			selectedLocalizedInfo,
		),
	});

	const data = useMemo<CustomMetadataColumn[]>(() => {
		const rows: CustomMetadataColumn[] = [];

		if (isFeatureEnabledForCurrentTenant('custom-field-localized-info-customFieldExample')) {
			rows.push({
				name: 'customFieldExample',
				value: stringAutoSave?.readOnlyValue?.displayValue || '',
				type: 'string',
				autoSave: stringAutoSave,
			});
		}

		if (isFeatureEnabledForCurrentTenant('custom-field-localized-info-customBooleanField')) {
			const value = booleanAutoSave.readOnlyValue?.displayValue;
			rows.push({
				name: 'customBooleanField',
				value: value == null ? '' : value.toString(),
				type: 'boolean',
				autoSave: booleanAutoSave,
			});
		}

		if (isFeatureEnabledForCurrentTenant('custom-field-localized-info-pullQuote')) {
			rows.push({
				name: 'Pull Quote',
				value: pullQuoteAutoSave.readOnlyValue?.displayValue ?? '',
				type: 'string',
				autoSave: pullQuoteAutoSave,
			});
		}

		if (isFeatureEnabledForCurrentTenant('custom-field-localized-info-staffDescription')) {
			rows.push({
				name: 'Staff Description',
				value: staffDescriptionAutoSave.readOnlyValue?.displayValue ?? '',
				type: 'string',
				autoSave: staffDescriptionAutoSave,
			});
		}

		return rows;
	}, [stringAutoSave, booleanAutoSave, pullQuoteAutoSave, staffDescriptionAutoSave]);

	return (
		<LocalizedWrapper>
			<CustomMetadataBase data={data} />
		</LocalizedWrapper>
	);
}
